/**
 * ImprovementConversationSheet — the improvement conversation panel (design brief screen 1).
 *
 * A right-side sheet at 460px from 768px up (the page that opened it stays visible behind a 50%
 * scrim) and a bottom sheet below that. The same panel renders full page at
 * /agent-conversations/:conversationId (ImprovementConversationPage), the deep link.
 *
 * Reading order: header, the anchor card (what the agent sent), the thread, the proposal. The
 * primary action is the proposal's Confirm change; nothing changes until it is pressed.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Send, X } from 'lucide-react';
import type { PlaybookAgentKind } from '@resida/api-types/agent';
import { IMPROVEMENT_TEXT_MAX } from '@resida/api-types/agent';
import { Button } from '../../ui/button';
import { Sheet, SheetDescription, SheetOverlay, SheetPortal, SheetTitle } from '../../ui/sheet';
import { Skeleton } from '../../ui/skeleton';
import { PLAYBOOK_PROPERTIES_QUERY_KEY, listPlaybookProperties } from '../../../api/agent-playbooks.api';
import { agentName } from '../../../lib/playbook';
import { ConversationOutcome } from './ConversationOutcome';
import type { ConflictChoice } from './ConflictChoices';
import type { OpenConversationRequest } from './ImprovementConversationProvider';
import { ProposalCard } from './ProposalCard';
import { useImprovementConversation } from './useImprovementConversation';

/** Narrow is a landlord message that yields a new pending proposal; nothing is saved (BR-12). */
const NARROW_MESSAGE = "Let's narrow one of them so they don't clash.";

function panelTitle(agentKind: PlaybookAgentKind | undefined): string {
  return agentKind ? `Improve the ${agentName(agentKind).toLowerCase()}` : 'Improve the agent';
}

function ThinkingDots() {
  return (
    <div role="status" data-testid="improvement-thinking" className="flex items-center gap-2 text-sm text-muted-foreground">
      <span className="h-2 w-2 animate-pulse rounded-full bg-muted-foreground" aria-hidden="true" />
      <span className="h-2 w-2 animate-pulse rounded-full bg-muted-foreground/60 [animation-delay:150ms]" aria-hidden="true" />
      <span className="h-2 w-2 animate-pulse rounded-full bg-muted-foreground/30 [animation-delay:300ms]" aria-hidden="true" />
      <span>The agent is thinking…</span>
    </div>
  );
}

interface PanelProps {
  request: OpenConversationRequest;
  onClose: () => void;
  title: (agentKind: PlaybookAgentKind | undefined) => ReactNode;
  subtitle: ReactNode;
  /** The sheet's built-in drag handle is only shown for the bottom sheet. */
  dragHandle: boolean;
  /** Move focus to the reply box once it is on screen (the sheet; the full page leaves focus alone). */
  focusReply?: boolean;
}

function ImprovementConversationPanel({ request, onClose, title, subtitle, dragHandle, focusReply = false }: PanelProps) {
  const conversation = useImprovementConversation({
    conversationId: request.conversationId,
    start: request.start,
    onStartFailed: request.onStartFailed,
  });
  const { view } = conversation;
  const [draft, setDraft] = useState('');
  const threadEndRef = useRef<HTMLDivElement>(null);
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const replyFocused = useRef(false);
  // The anchor card is open on a laptop and collapsed behind "What the agent sent" on a phone.
  const [anchorOpen, setAnchorOpen] = useState(() => window.matchMedia?.('(min-width: 768px)').matches ?? true);

  const { data: properties } = useQuery({
    queryKey: PLAYBOOK_PROPERTIES_QUERY_KEY,
    queryFn: listPlaybookProperties,
    staleTime: 60_000,
    retry: false,
  });
  const propertyName = (id: string) =>
    (id === request.property?.id ? request.property.name : undefined) ?? properties?.find((p) => p.id === id)?.name;

  useEffect(() => {
    threadEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [conversation.turns.length, conversation.thinking, view?.proposal?.proposalId, view?.state]);

  const agentKind = request.agentKind ?? view?.anchor.agentKind;
  const rulesPath = agentKind ? `/settings/agents/${agentKind}/playbook` : '/settings/agents';
  const closed = view?.state === 'closed';
  const budgetReached = view !== undefined && !closed && view.budget.messagesLeft === 0;
  const composerOpen = !closed && !budgetReached && !conversation.gone;
  const proposal = view?.state === 'proposal_pending' ? view.proposal : undefined;
  const conflict = view?.conflict ?? proposal?.conflict;
  // Initial focus goes to the reply box as soon as it exists, once.
  useEffect(() => {
    if (focusReply && composerOpen && !replyFocused.current && replyRef.current) {
      replyFocused.current = true;
      replyRef.current.focus();
    }
  }, [focusReply, composerOpen]);

  // A first message that never reached the agent goes back into the reply box, so "Your message
  // wasn't lost" is true and the landlord can resend it.
  const { unsentText } = conversation;
  useEffect(() => {
    if (unsentText) setDraft((d) => (d === '' ? unsentText : d));
  }, [unsentText]);

  const trimmed = draft.trim();
  const canSend = composerOpen && trimmed.length > 0 && !conversation.thinking && !conversation.busy;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    conversation.send(trimmed);
    setDraft('');
  };

  const retry = () => {
    if (unsentText && draft === unsentText) setDraft('');
    conversation.retry();
  };

  const handleChoice = (choice: ConflictChoice) => {
    if (choice === 'narrow') conversation.send(NARROW_MESSAGE);
    else if (proposal) conversation.confirm(proposal, choice);
  };

  // A refused proposal action (Confirm, Not quite, re-route) has no message to re-send: the proposal stays
  // on screen and its own buttons try again.
  const fromAction = conversation.errorSource === 'action';
  const errorBox =
    conversation.error === 'rate_limited' ? (
      <div
        role="alert"
        data-testid="improvement-rate-limited"
        className="flex flex-col gap-2 rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning"
      >
        <span>
          You've sent a lot of messages in the last hour. Try again in a little while.
          {fromAction ? ' Nothing was changed.' : " Your message wasn't lost."}
        </span>
        {!fromAction && (
          <Button type="button" variant="outline" className="self-start" onClick={retry} data-testid="improvement-retry">
            Retry
          </Button>
        )}
      </div>
    ) : conversation.error === 'unavailable' ? (
      <div
        role="alert"
        data-testid="improvement-unavailable"
        className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
      >
        <span className="text-destructive">
          {fromAction
            ? "The agent isn't available right now. Nothing was changed. Try again in a moment."
            : "The agent isn't available right now. Your message wasn't lost."}
        </span>
        {!fromAction && (
          <Button type="button" variant="outline" className="self-start" onClick={retry} data-testid="improvement-retry">
            Retry
          </Button>
        )}
      </div>
    ) : conversation.error === 'refused' || conversation.error === 'unknown' ? (
      <div
        role="alert"
        data-testid="improvement-error"
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
      >
        Something went wrong and nothing was changed. Try again.
      </div>
    ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="improvement-panel" data-conversation-id={conversation.conversationId ?? ''}>
      {dragHandle && <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-border md:hidden" aria-hidden="true" />}
      <header className="flex items-start gap-3 border-b border-border py-3 pl-4 pr-2 md:pl-6 md:pt-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {title(request.agentKind ?? view?.anchor.agentKind)}
          {subtitle}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0"
          aria-label="Close"
          onClick={onClose}
          data-testid="improvement-close"
        >
          <X className="h-[18px] w-[18px]" aria-hidden="true" />
        </Button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4 md:px-6 md:py-5" data-testid="improvement-thread">
        {conversation.gone ? (
          <div data-testid="improvement-gone" className="flex flex-col items-start gap-3 py-6">
            <p className="m-0 text-base font-semibold text-foreground">This conversation is no longer available</p>
            <p className="m-0 text-sm text-muted-foreground">It may have expired. You can start a new one from the agent's message or the case.</p>
            <Button type="button" variant="outline" onClick={onClose} data-testid="improvement-gone-close">
              Close
            </Button>
          </div>
        ) : conversation.loading ? (
          <div aria-busy="true" data-testid="improvement-loading" className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-10 w-2/3 self-end" />
          </div>
        ) : conversation.readFailed ? (
          <div role="alert" data-testid="improvement-unavailable" className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <span className="text-destructive">We couldn't load this conversation.</span>
            <Button type="button" variant="outline" className="self-start" onClick={() => void conversation.retryRead()} data-testid="improvement-retry-read">
              Retry
            </Button>
          </div>
        ) : (
          <>
            {request.anchorCard && (
              <details
                open={anchorOpen}
                onToggle={(e) => setAnchorOpen((e.target as HTMLDetailsElement).open)}
                data-testid="improvement-anchor"
                className="rounded-lg bg-muted px-3 py-2.5"
              >
                <summary className="flex min-h-[44px] cursor-pointer items-center text-xs font-semibold uppercase text-muted-foreground md:hidden">
                  What the agent sent
                </summary>
                <div className="flex flex-col gap-1.5 md:mt-0">
                  <div className="hidden text-xs font-semibold uppercase text-muted-foreground md:block" data-testid="improvement-anchor-label">
                    {request.anchorCard.label}
                    {request.anchorCard.at ? ` · ${request.anchorCard.at}` : ''}
                  </div>
                  <div className="mt-1.5 whitespace-pre-wrap text-sm text-foreground md:mt-0" data-testid="improvement-anchor-text">
                    {request.anchorCard.text}
                  </div>
                </div>
              </details>
            )}

            <div role="log" aria-live="polite" aria-label="Conversation" className="flex flex-col gap-4" data-testid="improvement-log">
            {conversation.turns.map((turn) =>
              turn.role === 'landlord' ? (
                <div
                  key={turn.turnId}
                  data-testid="improvement-turn-landlord"
                  className="max-w-[85%] self-end whitespace-pre-wrap rounded-xl rounded-br-sm bg-primary/10 px-3.5 py-2.5 text-sm text-foreground"
                >
                  {turn.text}
                </div>
              ) : (
                <div
                  key={turn.turnId}
                  data-testid="improvement-turn-agent"
                  className="max-w-[92%] self-start whitespace-pre-wrap text-sm leading-normal text-foreground"
                >
                  {turn.text}
                </div>
              ),
            )}
            </div>

            {conversation.thinking && !closed && <ThinkingDots />}

            {conversation.turnFailed && (
              <div
                role="alert"
                data-testid="improvement-turn-failed"
                className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
              >
                <span className="text-destructive">The agent couldn't reply. Your message wasn't lost.</span>
                <Button type="button" variant="outline" className="self-start" onClick={conversation.retry} disabled={conversation.busy} data-testid="improvement-retry">
                  Retry
                </Button>
              </div>
            )}

            {proposal && !conversation.thinking && (
              <ProposalCard
                proposal={proposal}
                conflict={conflict}
                propertyName={proposal.scope.kind === 'property' ? propertyName(proposal.scope.propertyId) : undefined}
                anchorProperty={request.property}
                busy={conversation.busy}
                budgetReached={budgetReached}
                onConfirm={() => conversation.confirm(proposal)}
                onNotQuite={() => conversation.notQuite(proposal)}
                onSwitchScope={conversation.send}
                onChoose={handleChoice}
              />
            )}

            {budgetReached && (
              <p className="m-0 text-sm text-muted-foreground" data-testid="improvement-budget-note">
                {proposal ? (
                  "We've been going back and forth for a while. Here's my best version. Confirm it, or edit your rules directly."
                ) : (
                  <>
                    We've been going back and forth for a while. Edit your rules directly in{' '}
                    <Link to={rulesPath} className="font-medium text-primary underline underline-offset-2" data-testid="improvement-budget-rules-link">
                      Settings → AI agents
                    </Link>
                    .
                  </>
                )}
              </p>
            )}

            {view && closed && view.outcome && (
              <ConversationOutcome
                view={view}
                outcome={view.outcome}
                confirmed={conversation.confirmed}
                propertyName={propertyName}
                busy={conversation.busy}
                onReroute={conversation.reroute}
                onDone={onClose}
              />
            )}

            {errorBox}
            <div ref={threadEndRef} />
          </>
        )}
      </div>

      {!conversation.gone && (composerOpen || budgetReached) && (
        <footer className="flex flex-col gap-2 border-t border-border px-4 pb-5 pt-3 md:px-6 md:py-4">
          {composerOpen ? (
            <form onSubmit={submit} className="flex items-end gap-2">
              <label htmlFor="improvement-reply" className="sr-only">
                Reply to the agent
              </label>
              <textarea
                id="improvement-reply"
                ref={replyRef}
                data-testid="improvement-reply"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
                }}
                rows={2}
                maxLength={IMPROVEMENT_TEXT_MAX}
                placeholder="Reply to the agent…"
                className="min-h-[44px] flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2.5 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
              />
              <Button type="submit" size="icon" className="h-11 w-11 shrink-0" aria-label="Send" disabled={!canSend} data-testid="improvement-send">
                <Send aria-hidden="true" />
              </Button>
            </form>
          ) : budgetReached ? (
            <p className="m-0 text-xs text-muted-foreground" data-testid="improvement-composer-closed">
              The reply box is closed for this conversation.
            </p>
          ) : null}
          {composerOpen && (
            <p className="m-0 text-xs text-muted-foreground">
              Nothing changes until you confirm. You can edit or remove any rule later in Settings → AI agents.
            </p>
          )}
        </footer>
      )}
    </div>
  );
}

/** The panel as a sheet over the page that opened it. */
export function ImprovementConversationSheet({
  request,
  onClose,
  returnFocusTo,
}: {
  request: OpenConversationRequest;
  onClose: () => void;
  /** The control that opened the sheet; focus goes back to it on close. */
  returnFocusTo?: HTMLElement | null;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetPortal>
        <SheetOverlay className="bg-black/50" />
        <DialogPrimitive.Content
          ref={contentRef}
          data-testid="improvement-sheet"
          // The reply box takes focus itself once it renders; the dialog never lands on Close.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            contentRef.current?.focus();
          }}
          onCloseAutoFocus={(e) => {
            if (returnFocusTo?.isConnected) {
              e.preventDefault();
              returnFocusTo.focus();
            }
          }}
          className="fixed inset-x-0 bottom-0 top-6 z-50 flex flex-col rounded-t-2xl border-t border-border bg-card shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom md:inset-y-0 md:left-auto md:right-0 md:top-0 md:w-[460px] md:max-w-full md:rounded-none md:border-l md:border-t-0 md:data-[state=closed]:slide-out-to-right md:data-[state=open]:slide-in-from-right"
        >
          <ImprovementConversationPanel
            request={request}
            onClose={onClose}
            dragHandle
            focusReply
            title={(agentKind) => <SheetTitle className="text-[17px] md:text-lg">{panelTitle(agentKind)}</SheetTitle>}
            subtitle={
              <SheetDescription className="text-xs md:text-[13px]" data-testid="improvement-subtitle">
                {request.subtitle ?? 'Tell the agent what to do differently.'}
              </SheetDescription>
            }
          />
        </DialogPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}

/** Route /agent-conversations/:conversationId — the same panel, full page (deep link). */
export function ImprovementConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  return (
    <div className="mx-auto flex h-[calc(100dvh-8rem)] min-h-[480px] max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card" data-testid="improvement-conversation-page">
      <ImprovementConversationPanel
        key={conversationId}
        request={{ conversationId }}
        onClose={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))}
        dragHandle={false}
        title={(agentKind) => <h1 className="m-0 text-lg font-semibold text-foreground">{panelTitle(agentKind)}</h1>}
        subtitle={<p className="m-0 text-[13px] text-muted-foreground">Tell the agent what to do differently.</p>}
      />
    </div>
  );
}

export default ImprovementConversationSheet;
